import React, { useState, useRef } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useCountriesList, useClickOutside } from '@/hooks';
import { Country } from '@/types/model';

interface CountryDropdownProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  label?: string;
  error?: string;
  helperText?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  className?: string;
}

const CountryDropdown = React.forwardRef<HTMLInputElement, CountryDropdownProps>(({
  label,
  error,
  helperText,
  placeholder = 'Select a country',
  value,
  onChange,
  disabled,
  name,
  onBlur,
  className = '',
  emptyMessage = 'No countries available',
  loadingMessage = 'Loading countries...',
  errorMessage = 'Unable to load countries',
  ...inputProps
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const { data, isLoading, isError } = useCountriesList();
  const countries = data || [];

  const selectedCountry = countries.find((country) => country.id === value);

  const toggleDropdown = () => {
    if (disabled || isLoading || isError) {
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (country: Country) => {
    onChange?.(country.id);
    setIsOpen(false);
    //onBlur?.();
  };

  const renderFlag = (country: Country) => {
    if (!country.flag) {
      return (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-500">
          {(country.nameEnglish || country.name).slice(0, 2).toUpperCase()}
        </span>
      );
    }

    if (/^https?:\/\//.test(country.flag)) {
      return (
        <img
          src={country.flag}
          alt={`${country.nameEnglish || country.name} flag`}
          className="h-6 w-9 object-cover"
        />
      );
    }

    return (
      <span className="text-lg" aria-hidden="true">
        {country.flag}
      </span>
    );
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <input
        type="hidden"
        name={name}
        value={value ?? ''}
        onBlur={onBlur}
        ref={ref}
        {...inputProps}
      />
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled || isLoading}
          className={`
            flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm
            focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
            ${disabled ? 'cursor-not-allowed bg-gray-50 text-gray-500' : 'cursor-pointer'}
            ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
          `}
        >
          <span className="flex min-h-[1.5rem] flex-1 items-center gap-2 truncate text-gray-900">
            {selectedCountry ? (
              <>
                {renderFlag(selectedCountry)}
                <span className="truncate">
                  {selectedCountry.name}
                </span>
              </>
            ) : (
              <span className="text-gray-400">
                {isLoading ? loadingMessage : placeholder}
              </span>
            )}
          </span>
          <ChevronDown
            size={16}
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {isLoading && (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-gray-500">
                <Loader2 size={16} className="animate-spin text-gray-400" />
                {loadingMessage}
              </div>
            )}

            {isError && !isLoading && (
              <div className="px-3 py-4 text-sm text-red-500">
                {errorMessage}
              </div>
            )}

            {!isLoading && !isError && countries.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500">
                {emptyMessage}
              </div>
            )}

            {!isLoading && !isError && countries.length > 0 && (
              <ul className="py-1">
                {countries.map((country) => (
                  <li key={country.id}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        country.id === value ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700'
                      }`}
                      onClick={() => handleSelect(country)}
                    >
                      {renderFlag(country)}
                      <div className="flex-1 truncate">
                        <div className="truncate text-sm font-medium text-gray-900">{country.name}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        helperText && <p className="text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
});

CountryDropdown.displayName = 'CountryDropdown';

export default CountryDropdown;
